/**
 * Lightweight, sampled diagnostics for SOFA payload anomalies.
 *
 * Goals:
 * - Capture which fields are missing/invalid on offending releases so we can
 *   reproduce demo-time crashes without flooding the console.
 * - Persist the latest few samples in sessionStorage so a user can copy them
 *   from the error boundary's "Copy error details" button later.
 */

const STORAGE_KEY = "__sofa_diagnostics";
const MAX_SAMPLES = 25;
const SEEN_KEYS = new Set<string>();

type SofaDiagnosticEvent = {
  source: string;
  reason: string;
  fields: Record<string, unknown>;
  timestamp: string;
};

function safeFieldSnapshot(release: any): Record<string, unknown> {
  if (!release || typeof release !== "object") {
    return { value: release === undefined ? "undefined" : String(release) };
  }
  return {
    ProductName: release.ProductName ?? release.productName ?? null,
    ProductVersion: release.ProductVersion ?? release.productVersion ?? null,
    UpdateName: release.UpdateName ?? release.updateName ?? null,
    ReleaseDate: release.ReleaseDate ?? release.releaseDate ?? null,
    keys: Object.keys(release).slice(0, 12),
  };
}

export function recordSofaAnomaly(source: string, reason: string, release: any) {
  const fields = safeFieldSnapshot(release);
  // De-dupe by source+reason+ProductName+ProductVersion to avoid log spam
  const dedupeKey = `${source}|${reason}|${fields.ProductName}|${fields.ProductVersion}`;
  if (SEEN_KEYS.has(dedupeKey)) return;
  SEEN_KEYS.add(dedupeKey);

  const event: SofaDiagnosticEvent = {
    source,
    reason,
    fields,
    timestamp: new Date().toISOString(),
  };

  // Console log (single line, structured) — visible during demos
  // eslint-disable-next-line no-console
  console.warn("[SOFA diagnostic]", event);

  // Persist to sessionStorage (FIFO, capped)
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const buffer: SofaDiagnosticEvent[] = raw ? JSON.parse(raw) : [];
    buffer.push(event);
    while (buffer.length > MAX_SAMPLES) buffer.shift();
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(buffer));
  } catch {
    // sessionStorage unavailable — ignore
  }
}

export function getSofaDiagnostics(): SofaDiagnosticEvent[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}
